import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInitiateRoboCall } from "@/components/InitiateRoboCallButton/useInitiateRoboCall";
import { DEPARTMENT_REQUEST_AGENT_IDS_QUERY_KEY, useDepartmentReuqestAgentIds } from "@/components/RequestData/hooks/useAgentHistory";
import { type AnalyticValues, useAnalytics } from "@/contexts/analytics/hooks";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import SystemDialog from "@/ui/Popups/Dialog";
import type { OpenShiftRequest, ReplaceShiftRequest } from "@/utils/models";
import { callOffBypassHours, callOffBypassWindowOf } from "@/utils/utilFunctions";
import { roboCallRowCopy } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** The posted sheet's own event properties (department, request, type). */
	properties: AnalyticValues;
	testIdPrefix: string;
};

/**
 * "Start a robo call" on a posted shift (P3-10): `useInitiateRoboCall`, under the CallOffAgent flag and inside the same
 * window `InitiateRoboCallButton` offers it in (from the admin bypass hours before the start until the bypass hours after
 * it), and not while one is already out; a shift past that window says the call is no longer available rather than when
 * it opens. Confirmed first with the Requests screen's own mobile confirm, word for word.
 */
export const GapsPostedRoboCallRow: FC<Props> = ({ request, properties, testIdPrefix }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment, timezone } = useHospitalData();
	const isRoboCallEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const queryClient = useQueryClient();
	const { data: roboCallRequestIds } = useDepartmentReuqestAgentIds();
	const { mutate: initiateRoboCall, isPending: isStartingRoboCall } = useInitiateRoboCall();
	const [isConfirming, setIsConfirming] = useState<boolean>(false);

	// DEFAULT: while the agent's list is still loading (or the flag is off, and it is never fetched) no call is known to be
	// out — the Requests screen's own button reads it the same way.
	const isRoboCallRunning = roboCallRequestIds?.includes(request.id) ?? false;
	const robo = roboCallRowCopy(isRoboCallRunning, callOffBypassWindowOf(request.message.shift_start, chosenDepartment, timezone));

	const handleStart = useCallback((): void => {
		setIsConfirming(false);
		track("gaps_posted_robo_call_start", properties);
		initiateRoboCall(
			{ requestId: request.id },
			{
				onSuccess: () => {
					track("gaps_posted_robo_call_success", properties);
					// The agent's list is what says a call is out; refetched now, the row reads "running" instead of offering a
					// second call until its three-minute cache happens to expire.
					void queryClient.invalidateQueries({ queryKey: [DEPARTMENT_REQUEST_AGENT_IDS_QUERY_KEY] });
				},
				// The hook's own snackbar says it failed; this records where from.
				onError: (error) => trackError("gaps_posted_robo_call_error", { ...properties, error_message: error.message }),
			},
		);
	}, [initiateRoboCall, properties, queryClient, request.id, track, trackError]);

	if (!isRoboCallEnabled) return null;
	return (
		<>
			<SheetActionRow
				icon={<HeadsetMicOutlinedIcon />}
				label={t(robo.labelKey)}
				description={t(robo.descriptionKey, { hours: callOffBypassHours(chosenDepartment).beforeShift })}
				showChevron={robo.isActionable}
				disabled={!robo.isActionable}
				loading={isStartingRoboCall}
				onClick={() => {
					track("gaps_posted_robo_call_confirm_shown", properties);
					setIsConfirming(true);
				}}
				dataTestId={`${testIdPrefix}-robo-call`}
			/>
			<SystemDialog
				dataTestId={`${testIdPrefix}-robo-call-dialog`}
				open={isConfirming}
				onClose={() => setIsConfirming(false)}
				title={t("robocall.initiate.confirm.title")}
				body={t("robocall.initiate.confirm.body")}
				confirm={t("robocall.initiate.confirm.confirm")}
				onConfirm={handleStart}
			/>
		</>
	);
};
